import { Warranty } from '@api/types';
import { TextCodes } from '@api/types/translation/TextCodes';
import {
  useAddWarrantyClaimAttachmentsMutation,
  useAddWarrantyClaimMutation,
} from '@api/warranty.api';
import {
  WarrantyBrandsForm,
  WarrantyCreateForm,
  useWarrantyClaimContext,
  useWarrantyStepperContext,
} from '@components';
import { useSerialNumber, useToast } from '@hooks';
import CloseIcon from '@mui/icons-material/Close';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Snackbar,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import { t } from '@translations';
import { useEffect, useMemo } from 'react';

export const WarrantyCreateStepper = () => {
  const { activeStep, completed, steps, handleReset, handleSetActiveStep } =
    useWarrantyStepperContext();
  const { form } = useWarrantyClaimContext();
  const [request] = useAddWarrantyClaimMutation();
  const [addAttachment] = useAddWarrantyClaimAttachmentsMutation();
  const { toast } = useToast();
  const { sanitizeSerialNo } = useSerialNumber();
  const onSubmit = async (data: Warranty) => {
    data.createdDate = new Date().toISOString();
    try {
      if (data.serialNo) data.serialNo = sanitizeSerialNo(data.serialNo);
      const response = await request(data).unwrap();
      const claimId = response;

      if (data?.recipeFile?.length && claimId) {
        const formData = new FormData();
        formData.append('file', data.recipeFile[0]);
        await addAttachment({
          warrantyClaimId: claimId,
          formData,
        }).unwrap();
      }

      toast({
        severity: 'success',
        message: t(TextCodes.Warranty.Block.CreateSuccess),
      });
      handleReset();
    } catch (error) {
      console.error(error);

      toast({
        severity: 'error',
        message: t(TextCodes.Warranty.Block.WarrantyClaimCreateError),
      });
    }
  };

  const stepContent = useMemo(() => {
    return [<WarrantyBrandsForm />, <WarrantyCreateForm />];
  }, []);

  useEffect(() => {
    const savedData = localStorage.getItem('warrantyFormData');
    if (savedData) {
      handleSetActiveStep(1);
    }
  }, [handleSetActiveStep]);

  return (
    <>
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label, index) => (
          <Step key={label} completed={completed[index]}>
            <StepLabel color='inherit'>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Box mt={4}>
        <form onSubmit={form.handleSubmit(onSubmit)}>
          <Box>{stepContent[activeStep]}</Box>
          <ActionButtons />
        </form>
      </Box>
    </>
  );
};

const ActionButtons = () => {
  const {
    activeStep,
    totalSteps,
    handleBack,
    handleNext,
    handleSaveDraft,
    hasDraft,
    showAlert,
    handleCloseAlert,
    handleReset,
  } = useWarrantyStepperContext();
  const { form, isSerialNoValid } = useWarrantyClaimContext();
  const selectedBrand = form.watch('brand');
  const serialNo = form.watch('serialNo');
  const isDisabled = (selectedBrand === 'Honda' && !serialNo) || isSerialNoValid === null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
      <Button color='inherit' variant='ghost' disabled={activeStep === 0} onClick={handleBack}>
        {t(TextCodes.Base.Back)}
      </Button>
      <Box sx={{ flex: '1 1 auto' }} />
      <Stack direction='row' spacing={2}>
        {hasDraft && (
          <Button variant='ghost' onClick={handleReset}>
            {t(TextCodes.Warranty.Block.RemoveDraft)}
          </Button>
        )}
        {activeStep === totalSteps() - 1 && (
          <Button variant='primary' size='small' onClick={handleSaveDraft}>
            {t(TextCodes.Warranty.Block.SaveDraft)}
          </Button>
        )}
        {activeStep === totalSteps() - 1 && (
          <Button type='submit' variant='primary' size='small'>
            {t(TextCodes.Warranty.Block.Create)}
          </Button>
        )}
        {activeStep !== totalSteps() - 1 && (
          <Button
            variant='primary'
            size='small'
            onClick={handleNext}
            disabled={isDisabled && (selectedBrand === 'Honda' || selectedBrand === 'Cramer')}
          >
            {t(TextCodes.Warranty.Block.Next)}
          </Button>
        )}
      </Stack>
      <Snackbar open={showAlert} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          severity='info'
          sx={{ mb: 2, minWidth: 200, maxWidth: { xs: 'auto', md: 400 } }}
          action={
            <IconButton size='small' aria-label='close' color='inherit' onClick={handleCloseAlert}>
              <CloseIcon fontSize='small' />
            </IconButton>
          }
        >
          <Typography variant='body1'>
            {t(TextCodes.Warranty.Block.FormSavedAsDraft)}{' '}
            <Box
              component='span'
              onClick={handleReset}
              sx={{ textDecoration: 'underline', cursor: 'pointer' }}
            >
              {t(TextCodes.Warranty.Block.ClickHere)}.
            </Box>
          </Typography>
        </Alert>
      </Snackbar>
    </Box>
  );
};
